/**
 * Shared types for DynamoDB table structures
 * These types define the data models used across the Idaho Pageant Applicant System
 */

// The immutable Form_Version snapshot shape is owned by the application-form
// pure module (its SSOT). Imported type-only so the stored `FormVersionItem`
// snapshot stays byte-for-byte consistent with what `snapshotForm` produces
// without introducing a runtime dependency (application-form-builder Req 8.1).
import type { FormVersionSnapshot } from '../lambda/application-form/fieldValidation';

// The Manual_Lock_Override enum is owned by the application-form pure module
// `phaseDeadlines.ts` (its single canonical definition — season-phase-deadlines
// task 3). Imported type-only so there is NO runtime dependency edge: although
// `phaseDeadlines.ts` imports the `SubmissionStatus` value from this file, this
// reverse import is elided at compile time, so no circular import is created.
import type { ManualLockOverride } from '../lambda/application-form/phaseDeadlines';

// Enums
export enum ApplicantType {
  MRS = 'MRS',
  MISS = 'MISS',
}

export enum SubmissionStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/**
 * Lifecycle_Stage — the contestant-journey standing within a Participation
 * (meridian-data-model Phase 3). Distinct from `applicationStatus`
 * (the application-workflow state): a person can be an `applicant`/`contestant`/
 * `titleholder`/`alumna` independently of whether their application is
 * `DRAFT`/`SUBMITTED`/`APPROVED`/`REJECTED` (Req 3.3, 3.4, 3.6).
 */
export enum LifecycleStage {
  APPLICANT = 'applicant',
  CONTESTANT = 'contestant',
  TITLEHOLDER = 'titleholder',
  ALUMNA = 'alumna',
}

/**
 * Admin standing — the admin lifecycle facet (meridian-data-model Phase 7, Req
 * 8.1). Tracked independently of the contestant-journey `LifecycleStage`: one
 * Person MAY hold both an admin standing and one or more contestant-facet
 * Participations, and each facet's standing is stored and changed on its own.
 *
 * `active` while an admin tenure is in effect; `alumna` once it ends. An alumna
 * admin retains Podium product access exactly like an alumna contestant (Req
 * 8.4). The transition between the two values is a lifecycle change ONLY — it
 * never writes a Role grant as a side effect (Req 8.2): access follows from the
 * standing through the Persona_Permission_Matrix, not from an imperative grant.
 */
export enum AdminStanding {
  ACTIVE = 'active',
  ALUMNA = 'alumna',
}

/**
 * Person_Identity_State — the identity CLASS a Person is in (tenant-foundation
 * Req 22.1). Every human is exactly one Person in one of three states:
 *
 *   - `CREDENTIALED` — has a login (a `cognitoSub` is present). The shipped
 *     authenticating user.
 *   - `MANAGED` — a minor acting under a Guardian with no credentials of their
 *     own (under the Tenant's configured COPPA age). Their data is
 *     guardian-managed and consent-gated per (child, Tenant) through the
 *     Membership consent state + the Consent_Ledger.
 *   - `SHADOW` — entered by a director or an import (e.g. a prior contestant),
 *     not yet claimed, no credentials. Claimed/linked to a login later
 *     (Req 22.5; the flow lives in the sibling `identity-lifecycle` spec).
 *
 * The state is a NON-KEY attribute on the STABLE Person (`personId` UUID PK is
 * unchanged — Req 22.2), so a Person CONVERTS between classes by mutating this
 * attribute in place, never by creating a new row. That is what "no orphaned
 * identity" means for the graduation (MANAGED→CREDENTIALED) and shadow-claim
 * conversions (Req 22.4): `personId` and every pointer at it (Memberships,
 * titles, photos, records) survives the transition. Those conversion FLOWS live
 * in `identity-lifecycle`; this enum is the model support. Under-18 handling
 * stays conservative across every state and transition (Req 22.7).
 */
export enum PersonIdentityState {
  CREDENTIALED = 'credentialed',
  MANAGED = 'managed',
  SHADOW = 'shadow',
}

export enum DocumentType {
  HEADSHOT = 'HEADSHOT',
  AD_PAGE = 'AD_PAGE',
  PHOTO_PAGE = 'PHOTO_PAGE',
  WEDDING_PHOTO = 'WEDDING_PHOTO',
  MARRIAGE_LICENSE = 'MARRIAGE_LICENSE',
  PROOF_OF_RESIDENCY = 'PROOF_OF_RESIDENCY',
  VOLUNTEER_FORM = 'VOLUNTEER_FORM',
  DIVORCE_DECREE = 'DIVORCE_DECREE',
  DEATH_CERTIFICATE = 'DEATH_CERTIFICATE',
}

export enum FieldType {
  TEXT = 'TEXT',
  LONG_TEXT = 'LONG_TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
  SELECT = 'SELECT',
  CHECKBOX = 'CHECKBOX',
  FILE_UPLOAD = 'FILE_UPLOAD',
}

export enum FormType {
  PHASE1 = 'PHASE1',
  /**
   * The consolidated phase-2 application form (prod-migration-cutover [FORM]
   * workstream). One `FormSchema` carrying the three post-phase-1 pages —
   * Stage Form, Interview Info, Placement Questions — whose legacy blob data
   * (`stageFormData` / `interviewInfoData` / `placementQuestionsData`) is
   * hydrated into `ApplicationResponse.values[fieldId]`. Authored in
   * `.kiro/specs/prod-migration-cutover/phase2-form-template.md` §A (decision D-6).
   */
  PHASE2 = 'PHASE2',
  STAGE_FORM = 'STAGE_FORM',
  INTERVIEW_INFO = 'INTERVIEW_INFO',
  PLACEMENT_QUESTIONS = 'PLACEMENT_QUESTIONS',
}

// Address types
export interface Address {
  city: string;
  state: string;
  zip: string;
  street?: string;
}

/**
 * PersonAddress — the current contact address captured at contact-intake and
 * promoted onto the durable `Person` SSOT (application-form-builder Req 14.6,
 * 23.1). Distinct from the legacy `Address` (whose `street` is optional): a
 * captured contact address requires a non-blank street so the identity record
 * is complete. Mirrors `PersonAddress` in
 * `infrastructure/lambda/application-form/contactIntake.ts` (kept consistent).
 */
export interface PersonAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface SchoolInfo {
  name: string;
  location: string;
}

export interface CollegeInfo {
  name: string;
  location: string;
  levelReached: string;
  degrees?: string[];
}

// Phase 1 data structure
export interface Phase1Data {
  homeAddress: Address;
  age: number;
  dateOfBirth: string;
  birthplace: string;
  usCitizen: boolean;
  eyeColor: string;
  hairColor: string;
  dressSize: string;
  swimsuitSize: string;
  shoeSize: string;
  occupation: string;
  employerAddress?: Address;
  secondarySchool: SchoolInfo;
  college?: CollegeInfo;
  currentCommunityInvolvement: string;
  recentCommunityInvolvement: string;
  hobbies: string;
  talents: string;
  generalBackground: string;
  // Mrs-specific fields
  spouseName?: string;
  marriageDate?: string;
  marriageConfirmed?: boolean;
  // Miss-specific fields
  maritalStatus?: 'SINGLE' | 'DIVORCED' | 'WIDOWED';
  // Optional referral field
  referredBy?: string;
}

// Stage form data structure
export interface StageFormData {
  stageName: string;
  height: string;
  hairColor: string;
  eyeColor: string;
  occupation: string;
  fitnessRoutine: string; // 30 word limit
  mostImportantLearning: string; // 50 word limit
  // Mrs-specific fields
  husbandName?: string;
  yearsMarried?: number;
}

// Interview info data structure
export interface InterviewInfoData {
  nameAndTitle: string;
  dateOfBirth: string;
  cityOfResidency: string;
  education: string;
  occupation: string;
  careerGoals: string;
  activitiesAndHobbies: string;
  communityInvolvement: string;
  leadershipRoles: string;
  interestingFacts: string;
  pageantAccomplishmentGoals: string;
  // Mrs-specific fields
  spouseName?: string;
  childrenNames?: string[];
  childrenAges?: number[];
}

// Placement questions data structure
export interface PlacementQuestionsData {
  top15Response: string; // 50 word limit
  interviewJudgesQuestion: string;
}

// DynamoDB Table Interfaces

/**
 * Applicant table - Core applicant identity, persists across competition years
 * Partition Key: id (UUID)
 * GSI: cognitoSub-index (cognitoSub)
 *
 * Identity fields (email, fullName, profilePhotoS3Key, lastLoginAt) are now
 * sourced from the User table via cognitoSub join. They have been removed from
 * this type to enforce the User table as the single source of truth.
 */
export interface Applicant {
  id: string;
  cognitoSub?: string; // FK to User table; used to join identity fields
  homePhone?: string;
  mobilePhone?: string;
  deletedAt?: string; // ISO 8601 timestamp for soft delete
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/**
 * ApplicantYearProfile table - Year-specific application data
 * Partition Key: applicantId
 * Sort Key: competitionYear
 * GSI1: submissionStatus-competitionYear-index
 */
export interface ApplicantYearProfile {
  applicantId: string;
  competitionYear: number;
  applicantType: ApplicantType;
  submissionStatus: SubmissionStatus;
  phase1Data?: Phase1Data;
  phase2Data?: Record<string, unknown>;
  stageFormData?: StageFormData;
  interviewInfoData?: InterviewInfoData;
  placementQuestionsData?: PlacementQuestionsData;
  placementQuestionsLocked: boolean;
  profileLocked: boolean;
  outstandingFees: boolean;
  /**
   * When true, the contestant's year profile is exempt from the Instagram-linking
   * requirement at approval time and is excluded from all season-scoped insights.
   * Optional on the TypeScript side because legacy rows predating this feature
   * won't have the attribute; resolvers default missing values to `false`.
   */
  instagramLinkingExempt?: boolean;
  digitalSignature?: string;
  signedAt?: string;
  directorNotes?: string;
  deletedAt?: string; // ISO 8601 timestamp for soft delete
  createdAt: string;
  updatedAt: string;
}

/**
 * Participation table - the Person↔Season bridge (meridian-data-model Phase 3).
 *
 * Evolves `ApplicantYearProfile` by re-anchoring the per-season application
 * record on a durable `personId` (was `applicantId`) and adding a stable
 * `participationId` plus the Lifecycle_Stage axis. It carries per-season
 * application data and "points up" at the Person SSOT — it stores ZERO Person
 * identity fields (name, email, phone, profile photo) (Req 3.1, 4.4).
 *
 * Partition Key: personId (re-anchored from applicantId)
 * Sort Key: competitionYear (four-digit calendar year, 2000–2100)
 * GSI1: participationId-index (participationId) — the stable handle Merit FKs
 *       point at; survives a Person being re-keyed (Req 3.2, 5.1).
 * GSI2: applicationStatus-competitionYear-index — application-workflow queries
 *       (replaces the legacy submissionStatus GSI).
 * GSI3: lifecycleStage-competitionYear-index — contestant-journey queries (new).
 *
 * Brownfield note: this is additive. The legacy `${stage}-ApplicantYearProfile`
 * table and its readers remain in place; the Phase-3 backfill (task 6.3)
 * populates this table by joining `applicantId → personId` and stamping
 * `participationId = uuidv5(personId#competitionYear)`.
 */
export interface ParticipationItem {
  personId: string; // PK (was applicantId)
  competitionYear: number; // SK (four-digit year, 2000–2100)
  participationId: string; // GSI: participationId-index (stable Merit FK target)
  /**
   * Tenant scope (tenant-foundation Req 2.1, 2.4, 2.5, 17.4) — the owning Tenant
   * for this tenant-scoped record. Additive + optional + NON-KEY, and DARK:
   * nothing stamps or enforces it until the tenant-foundation migration (the
   * Idaho backfill = `tenant-idaho`, then the enforcement flip). The record hangs
   * off the person's Membership in this Tenant. Global identity (`Person`/`User`)
   * deliberately does NOT carry `tenantId` — the tenant boundary is the
   * Membership, so only tenant-OWNED records carry this scope.
   */
  tenantId?: string;
  divisionKey?: string; // single-sourced division (division-registry)
  applicantType: ApplicantType;
  /**
   * Contestant-journey standing (Req 3.3, 3.4). Defaults to `applicant` on
   * create (Req 3.8). Changed independently of `applicationStatus` (Req 3.6).
   */
  lifecycleStage: LifecycleStage;
  /**
   * Application-workflow state (Req 3.3, 3.5). Defaults to `DRAFT` on create
   * (Req 3.8). Changed independently of `lifecycleStage` (Req 3.6).
   */
  applicationStatus: SubmissionStatus;
  requestedTitle?: string;
  approvedTitle?: string; // SSOT for title (Merit reads this — Phase 5)
  contestantNumber?: number; // single-sourced here (was also in Merit)
  /**
   * Randomized stage-presentation order within the division, set by the
   * director's "Randomize order" action (between cuts). Independent of
   * `contestantNumber` (the number is preserved; only the order changes). When
   * unset, the roster falls back to its default within-division ordering.
   */
  presentationOrder?: number;
  phase1Data?: Phase1Data;
  phase2Data?: Record<string, unknown>;
  stageFormData?: StageFormData;
  interviewInfoData?: InterviewInfoData;
  placementQuestionsData?: PlacementQuestionsData;
  placementQuestionsLocked: boolean;
  profileLocked: boolean;
  outstandingFees: boolean;
  instagramLinkingExempt?: boolean;
  digitalSignature?: string;
  signedAt?: string;
  directorNotes?: string;
  deletedAt?: string; // ISO 8601 timestamp for soft delete
  createdAt: string;
  updatedAt: string;
}

/**
 * VolunteerActivity table - Cumulative volunteer tracking across years
 * Partition Key: id (UUID)
 * GSI1: applicantId-activityDate-index (legacy anchor — kept readable during migration)
 * GSI2: personId-activityDate-index (Phase-3 re-anchor — points "up" at the Person SSOT)
 * GSI3: verificationStatus-activityDate-index (self-describing name == its keys, Req 4.6)
 */
export interface VolunteerActivity {
  id: string;
  applicantId: string;
  /**
   * The durable Person SSOT id this activity belongs to; partition key of the
   * `personId-activityDate-index`.
   *
   * Added by the `meridian-data-model` Phase 3 re-anchor (task 6.5). Optional
   * during the transition: `backfill-volunteer-activity-person.ts` resolves it
   * by joining `applicantId → personId` via the Person map and writes it only
   * when absent, so the legacy `applicantId-activityDate-index` keeps answering
   * until every reader cuts over (dual-anchor window, Req 6.3).
   */
  personId?: string;
  organizationName: string;
  activityDate: string; // ISO 8601 date
  hoursContributed: number;
  description: string;
  /**
   * The SINGLE stored verification source (meridian-data-model R4.5, task 10.2)
   * and the partition key of `verificationStatus-activityDate-index`.
   *
   * The derivable `verifiedByDirector` boolean is NO LONGER stored — it is
   * exposed only as a `@deprecated` computed GraphQL field equal to
   * `verificationStatus === "VERIFIED"`.
   */
  verificationStatus: 'PENDING' | 'VERIFIED';
  createdAt: string;
}

/**
 * MeritContestant — a judged contestant row linked back to its Participation.
 *
 * Today the merit-judging Lambda DERIVES contestants at query time from the
 * approved-applicant pipeline (no stored table — design §Data Models). This
 * type is the target stored shape the Phase-3 re-anchor introduces (design §6,
 * meridian-data-model task 6.5): every Merit row gains a required
 * `participationId` foreign key back to a Participation plus a denormalized
 * `personId` copied from that linked Participation, so Merit no longer re-joins
 * identity heuristically.
 *
 * Title, divisionKey, and contestantNumber are intentionally NOT stored here —
 * they remain single-sourced on the linked Participation and are read through
 * it (Req 5.3, 5.5; the duplicated Merit copies are removed in Phase 5,
 * task 10.5). The `resolveMeritContestantLink` /
 * `buildMeritContestantItem` validators in
 * `infrastructure/lambda/merit-judging/participation-link.ts` enforce that a row
 * is only ever built with a non-null `participationId` that resolves to an
 * existing Participation (Req 5.1, 5.2).
 */
export interface MeritContestantItem {
  id: string; // PK (unchanged)
  participationId: string; // REQUIRED FK -> Participation (participationId-index)
  personId: string; // denormalized from the linked Participation (direct person queries)
  competitionYear: number;
}

/**
 * RoleAssignment table - Time-bound role-history records (Community Phase 2)
 * Partition Key: id (UUID)
 * GSI: personId-grantedAt-index (meridian-data-model Phase 3 — durable Person
 *      SSOT anchor; the only access anchor after access-anchor-retirement A.6)
 * GSI: role-active-index (meridian-data-model Phase 3 — active grants per role
 *      for the drift reconciler; sort key activeGrantedAt = `${active}#${grantedAt}`)
 *
 * The legacy `subjectKey-<grantedAt>` GSI was REMOVED in access-anchor-retirement
 * Workstream A.6 once Gate A confirmed no reader used the legacy anchor and every
 * active row carried `personId`.
 *
 * Introduced by the `community-records` spec as the one new durable backend
 * entity for Phase 2. Mirrors the VolunteerActivity table shape (UUID id PK,
 * PAY_PER_REQUEST, PITR) and is seeded by an idempotent backfill from Cognito
 * group membership.
 */
export interface RoleAssignmentItem {
  id: string; // UUID (PK)
  /** Tenant scope — additive + optional + NON-KEY, DARK (tenant-foundation Req 2.1, 2.4, 2.5, 17.4; see ParticipationItem). Every RoleAssignment row is tenant-stamped once the Task-10 flip threads `tenantId` through `writeGrantRow`. */
  tenantId?: string;
  /**
   * The durable Person SSOT id this assignment belongs to; partition key of the
   * `personId-grantedAt-index` and the only access anchor (A.6).
   */
  personId: string;
  /**
   * LEGACY anchor (cognitoSub-when-present-else-applicantId). Retired in
   * access-anchor-retirement A.6: it is no longer the schema anchor or a GSI
   * key. Existing rows may still carry an inert `subjectKey` attribute, and the
   * re-anchor migration still reads it to resolve `personId`, so it is kept as
   * an optional field — but no writer stamps it any longer.
   */
  subjectKey?: string;
  role: 'admin' | 'poweruser' | 'director' | 'judge' | 'staff';
  grantedAt: string; // ISO 8601 (AWSDateTime); GSI sort key, newest-first listing
  /**
   * Composite sort key `${active}#${grantedAt}` (e.g. "true#2025-01-01T...")
   * for the `role-active-index` the Phase-4 drift reconciler scans. Stamped by
   * the Phase-3 backfill alongside `personId`; optional during the transition.
   */
  activeGrantedAt?: string;
  scope?: string; // Scope / season label, e.g. "2025 Season" (nullable)
  active: boolean; // Whether the assignment is currently in effect
  endedAt?: string; // ISO 8601 (AWSDateTime); set by the (deferred) end mutation (nullable)
  source: string; // "backfill:<group>" or "manual" — provenance for idempotency + audit
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/**
 * Person table - the durable, season-independent single source of truth (SSOT)
 * for one human's identity (name, email, phone, profile photo).
 *
 * Introduced by the `meridian-data-model` spec (Phase 1) as the durable human
 * record everything else "points up" at. A field belongs to Person only if it
 * stays true even if the human never applies again (the bifurcation test).
 *
 * Partition Key: personId (UUID)
 * GSI1: cognitoSub-index (cognitoSub) — auth-identity join; nullable for
 *       non-authenticating People, so rows with a null cognitoSub are absent
 *       from this index.
 * GSI2: email-index (primaryEmail) — lookup + case-insensitive uniqueness.
 * GSI3: directory-lastName-firstName-index (directoryPartition + lastName#firstName)
 *       — paged, alphabetically-sorted directory listing.
 */
export interface PersonItem {
  personId: string; // UUID (PK)
  legalFirstName: string;
  legalLastName: string;
  preferredName?: string;
  primaryEmail: string; // GSI: email-index (lookup + uniqueness)
  mobilePhone?: string;
  homePhone?: string;
  profilePhotoS3Key?: string; // SSOT for avatar — replaces all duplicated copies
  cognitoSub?: string; // GSI: cognitoSub-index; nullable for non-auth People
  /**
   * Admin standing facet (meridian-data-model Phase 7, Req 8.1) — `active` while
   * an admin tenure is in effect, `alumna` once it ends. Tracked independently
   * of any contestant-facet Participation, so one Person may hold both at once.
   *
   * Written only by Person_Service (Req 1.4) via the `setAdminStanding`
   * transition, which changes standing ONLY and never writes a Role grant as a
   * side effect (Req 8.2). Optional: a Person who has never been an admin has no
   * standing on this axis. An `alumna` admin retains Podium (Req 8.4).
   */
  adminStanding?: AdminStanding;
  /**
   * Identity class (tenant-foundation Req 22.1) — CREDENTIALED / MANAGED /
   * SHADOW (see {@link PersonIdentityState}). Additive + optional + NON-KEY, and
   * DARK until the tenant-foundation migration classifies existing rows: an
   * absent value denotes a legacy, pre-classification Person and is treated as
   * CREDENTIALED (the shipped authenticating user) during the dark phase.
   * Because this is a mutable attribute on the stable `personId` (Req 22.2), a
   * Person converts between classes in place with no orphaned identity (Req
   * 22.4). Under-18 subjects stay MANAGED and conservative regardless of state
   * (Req 22.7). `Person` stays GLOBAL — this never implies a `tenantId`; the
   * tenant boundary is the Membership.
   */
  identityState?: PersonIdentityState;
  /**
   * Verify-once parental status (tenant-foundation Req 21.2) — the durable,
   * PLATFORM-WIDE flag that this adult Person has completed verifiable parental
   * verification. Set ONCE at the parent-account level and never repeated per
   * program (Req 21.4); consent itself is recorded separately, per (child,
   * Tenant), as append-only Consent_Ledger entries (Req 21.3). Verification is a
   * platform-level fact about the adult, so it lives on the global Person, not
   * on any Membership. Additive + optional + NON-KEY; absent/false = unverified.
   * The flow that SETS this is Task 25.
   */
  verifiedParent?: boolean;
  /** ISO 8601 — when `verifiedParent` was set. Companion verify-once audit metadata (Req 21.2). */
  verifiedParentAt?: string;
  /**
   * The verification method used to set `verifiedParent` (e.g. the VPC method in
   * force at verification time). Companion audit metadata; the per-(child,
   * Tenant) consent method is recorded independently on each Consent_Ledger
   * entry (Req 21.3).
   */
  verifiedParentMethod?: string;
  /**
   * ISO 8601 date — the contact Birthday promoted from contact-intake onto the
   * durable Person SSOT (application-form-builder Req 14.6, 23.1). Additive +
   * optional: Person rows predating contact-intake won't carry it.
   */
  dateOfBirth?: string;
  /**
   * Current contact address promoted from contact-intake (Req 14.6, 23.1).
   * Additive + optional so existing Person rows stay valid.
   */
  address?: PersonAddress;
  /**
   * Welcome-email send-once indicator (director-form-application-cutover
   * Req 9.3). ISO 8601 timestamp stamped when the Welcome_Email is sent
   * successfully from `submitContactIntake`. Absent/null means the email has
   * not yet been sent, so the guard (`shouldSendWelcome`) sends at most once
   * across concurrent or repeated intake submissions. Additive + optional: it
   * belongs on Person (not Participation) because being welcomed is a per-human
   * fact that stays true even if the human never applies for a season.
   */
  welcomeEmailSentAt?: string;
  /**
   * Directory sort key: `${legalLastName}#${legalFirstName}` — GSI3 sort key
   * for the alphabetically-sorted community directory listing.
   */
  'lastName#firstName': string;
  /**
   * Constant partition ("ALL") for the directory GSI so the full Person set can
   * be queried as a single sorted, paginated partition.
   */
  directoryPartition: string;
  deletedAt?: string; // ISO 8601 soft delete (null = active record)
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/**
 * DocumentReference table - Metadata for S3-stored documents
 * Partition Key: id (UUID)
 * GSI1: applicantId-competitionYear-index
 */
export interface DocumentReference {
  id: string;
  /** Tenant scope — additive + optional + NON-KEY, DARK (tenant-foundation Req 2.1, 2.4, 2.5, 17.4; see ParticipationItem). */
  tenantId?: string;
  applicantId: string;
  competitionYear: number;
  documentType: DocumentType;
  s3Key: string;
  fileName: string;
  contentType: string;
  uploadedAt: string;
  /**
   * Links an upload to a `Document_Catalog_Entry` (application-form-builder
   * Req 20.2, 20.4). Additive + optional — legacy `DocumentType`-keyed uploads
   * carry no `catalogEntryId` and keep resolving unchanged. The catalog link is
   * resolved by the adapter, so the existing shape and key never break.
   */
  catalogEntryId?: string;
}

/**
 * application-form-builder shared value types (Req 16, 17, 21, 22).
 *
 * These string-literal unions mirror the SSOT definitions in the application-form
 * pure modules (`fieldValidation.ts` for `FieldCategory`/`BindingTarget`/
 * `Audience`/`Phase`, `pageApproval.ts` for `PageApprovalStatus`) and are kept
 * structurally identical so a stored row and the pure module agree exactly.
 */

/** Whether a Form_Field stores its own value (`CUSTOM`) or is backed by a canonical entity (`BOUND`) (Req 17). */
export type FieldCategory = 'CUSTOM' | 'BOUND';

/** The canonical entity a Bound_Field references — Binding_Target (Req 17, 18, 19, 20). */
export type BindingTarget =
  | 'DIVISION'
  | 'TITLE_PREFERENCES'
  | 'CONTACT_IDENTITY'
  | 'DOCUMENT_SLOT'
  | 'SEASON';

/** Who a page/field is visible to — Audience_Set (Req 22). */
export type Audience = 'APPLICANT' | 'DIRECTOR' | 'JUDGE';

/** The application Phase a page belongs to (Req 16). */
export type Phase = 'PHASE_1' | 'PHASE_2';

/** The per-page director-approval status — independent of `applicationStatus` (Req 21.2). */
export type PageApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/**
 * Help_Text_Surface formatting indicator (application-builder-rich-help-text
 * Req 2.1). Absent on a stored row ⇒ treated as PLAIN (Req 5.1). Additive.
 */
export type HelpTextFormat = 'PLAIN' | 'HTML';

/**
 * FormSchema table - Configuration-driven form definitions
 * Partition Key: id (UUID)
 * GSI1: formType-applicantType-competitionYear-index
 * GSI2: competitionId-isActive-index (application-form-builder — Merit competition scope)
 *
 * Evolved by application-form-builder (Req 7.4): gains a Merit `competitionId`
 * scope, an editable `title`, and `currentVersionNumber` (the latest published
 * Form_Version). All three are additive + optional so existing rows stay valid.
 */
export interface FormSchema {
  id: string;
  formType: FormType;
  applicantType: ApplicantType | 'BOTH';
  competitionYear: number;
  competitionId?: string; // Merit competition scope (additive)
  title?: string; // editable form title (additive)
  currentVersionNumber?: number; // latest published Form_Version (additive)
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * FormPage table - First-class page within an Application_Form (NEW —
 * application-form-builder Req 2.7, 16.1, 21.1, 22.1).
 * Partition Key: id (UUID)
 * GSI1: formSchemaId-displayOrder-index
 *
 * A page is a first-class entity with its own title, help text, order, and
 * visibility — plus the Phase, Audience_Set, and director-approval axes. As a
 * brand-new table it has no legacy rows, so `audienceSet`/`phase`/
 * `requiresDirectorApproval` are required here (the builder stamps documented
 * defaults: `[APPLICANT, DIRECTOR]` / `PHASE_1` / `false`).
 */
export interface FormPage {
  id: string;
  formSchemaId: string;
  /**
   * Stable, human-authored page key stamped by the seed (e.g. `'costume'`) —
   * NEW (approval-rollup-surfaces Design Decision D1). Additive/optional: legacy
   * rows have no `pageKey`, so consumers fall back to the persisted `id`. Used by
   * the Nav_Deriver for tie-break ordering (Req 1.3) and label fallback (Req 1.10).
   */
  pageKey?: string;
  title: string;
  helpText?: string; // EXISTING — remains PLAIN text always
  helpTextFormat?: HelpTextFormat; // NEW (rich-help-text) — additive; absent ⇒ PLAIN (Req 5.1)
  helpTextHtml?: string; // NEW (rich-help-text) — sanitized HTML; present iff helpTextFormat === 'HTML' (Req 2.2)
  autoCollapseHelp?: boolean; // NEW (rich-help-text) — Auto_Collapse_Config (Req 6); absent ⇒ false (Req 6.6)
  displayOrder: number;
  visibleDivisionKeys?: string[] | null; // null/empty = all active divisions
  audienceSet: Audience[]; // Req 22.1; default [APPLICANT, DIRECTOR]
  phase: Phase; // Req 16.1; default PHASE_1
  requiresDirectorApproval: boolean; // Req 21.1; default false
  competitionId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * FormField table - Individual field definitions within a schema
 * Partition Key: id (UUID)
 * GSI1: formSchemaId-displayOrder-index
 * GSI2: formPageId-displayOrder-index (application-form-builder — page association)
 *
 * Evolved by application-form-builder (Req 2.7, 17.1, 22.1). All new attributes
 * are additive + optional so existing rows stay valid; the adapter applies
 * documented defaults (e.g. `fieldCategory` → CUSTOM, `audienceSet` →
 * [APPLICANT, DIRECTOR]) when a legacy row omits them. `fieldType` is relaxed to
 * optional: it is present for CUSTOM fields and absent for BOUND fields.
 */
export interface FormField {
  id: string;
  formSchemaId: string;
  formPageId?: string; // page association (Req 2.7) — additive
  label: string;
  fieldCategory?: FieldCategory; // CUSTOM | BOUND (Req 17) — additive
  bindingTarget?: BindingTarget; // present iff fieldCategory === 'BOUND'
  fieldType?: FieldType; // present iff fieldCategory === 'CUSTOM'
  required: boolean;
  wordLimit?: number;
  characterLimit?: number;
  options?: string[];
  helpText?: string; // additive — remains PLAIN text always (rich-help-text Req 2.6)
  helpTextFormat?: HelpTextFormat; // NEW (rich-help-text) — additive; absent ⇒ PLAIN (Req 5.1)
  helpTextHtml?: string; // NEW (rich-help-text) — sanitized HTML; present iff helpTextFormat === 'HTML' (Req 2.2)
  allowedContentTypes?: string[]; // FILE_UPLOAD — additive
  maxFileSizeBytes?: number; // FILE_UPLOAD — additive
  maxFileCount?: number; // FILE_UPLOAD — additive
  displayOrder: number;
  immutableOnSubmit: boolean;
  thirdPersonRequired: boolean;
  applicantTypeVisibility: ApplicantType | 'BOTH'; // legacy, read-through only
  visibleDivisionKeys?: string[] | null; // additive
  audienceSet?: Audience[]; // Req 22.1 — additive
}

/**
 * The stored Response_Value for one Custom_Field within an
 * Application_Response, typed by Field_Type. A discriminated union mirroring
 * `ResponseValue` in `fieldValidation.ts` (kept consistent). Bound_Fields store
 * NO value — the no-stored-bound-value invariant (Req 17.2) holds on `values`.
 */
export type ResponseValueStored =
  | { kind: 'text'; value: string }
  | { kind: 'number'; value: number }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'date'; value: string }
  | { kind: 'list'; value: string[] }
  | {
      kind: 'files';
      value: { s3Key: string; fileName: string; contentType: string; sizeBytes: number }[];
    };

/**
 * FormVersion table - Immutable Form_Version snapshot (NEW —
 * application-form-builder Req 6.5, 8.1).
 * Partition Key: id (deterministic `uuidv5(formSchemaId#versionNumber)`)
 * GSI1: formSchemaId-versionNumber-index
 *
 * The `snapshot` carries the captured page attributes (phase / audienceSet /
 * requiresDirectorApproval) and field attributes (fieldCategory / bindingTarget
 * / audienceSet) so a response is always interpreted, gated, and projected
 * against the structure it was captured under (Req 8.3). The snapshot shape is
 * owned by `fieldValidation.ts` and imported type-only to stay consistent.
 */
export interface FormVersionItem {
  id: string; // uuidv5(formSchemaId#versionNumber)
  formSchemaId: string;
  versionNumber: number;
  competitionId?: string;
  snapshot: FormVersionSnapshot;
  createdAt: string;
}

/**
 * ApplicationResponse table - One applicant's response to an Application_Form
 * (NEW — application-form-builder Req 17.1, 19.2).
 * Partition Key: applicantPersonId
 * Sort Key: competitionId
 * GSI1: competitionId-submissionStatus-updatedAt-index
 *
 * `values` is a map of `fieldId → ResponseValueStored` for CUSTOM fields ONLY —
 * Bound_Fields never store a value here (Req 17.2). `titlePreferences` is the
 * reserved ranked-list slot held OUTSIDE `values` so the no-stored-bound-value
 * invariant on `values` is preserved (Req 19.2); `Participation.approvedTitle`
 * remains the title SSOT. `formVersionId` records the captured version (Req 6.5).
 */
export interface ApplicationResponseItem {
  applicantPersonId: string; // PK
  competitionId: string; // SK
  /** Tenant scope — additive + optional + NON-KEY, DARK (tenant-foundation Req 2.1, 2.4, 2.5, 17.4; see ParticipationItem). */
  tenantId?: string;
  formSchemaId: string;
  formVersionId: string; // captured version (Req 6.5)
  submissionStatus: SubmissionStatus;
  values: Record<string, ResponseValueStored>; // CUSTOM fields only (Req 17.2)
  titlePreferences?: string[]; // reserved ranked-list slot (Req 19.2)
  submittedAt?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Director "request changes" marker (applicant-application-flow). When a
   * director returns a SUBMITTED application for edits, the status is pushed
   * back to DRAFT and this flag is set true, so the applicant's
   * `resolveApplicantAppState` resolves the state to CHANGES_REQUESTED rather
   * than a plain in-progress DRAFT. Cleared on APPROVE / REJECT and on any
   * applicant re-submit. No new SubmissionStatus enum value is introduced.
   */
  reviewReturn?: boolean;
  /**
   * Contestant-visible feedback authored by a director on a REQUEST_CHANGES
   * decision (Return_Feedback_Note — application-decision-feedback Req 2, 4.2).
   * Persisted on the response and exposed on the OWNER read path plus the
   * director render envelope. ABSENT by default (no backfill) and left unset
   * when no note accompanied the return (Req 4.4). Cleared on resubmit
   * (DRAFT→SUBMITTED), on APPROVE / REJECT, and on REOPEN.
   */
  returnFeedbackNote?: string;
  /**
   * Director-only note authored on a REJECT decision (Internal_Reject_Note —
   * application-decision-feedback Req 3, 4.3). Persisted on the response and
   * NEVER present on any contestant-facing read model or GraphQL shape (Req 5);
   * surfaced ONLY on the director-gated render envelope. ABSENT by default (no
   * backfill) and left unset when no note accompanied the reject (Req 4.4).
   * Cleared on APPROVE / REQUEST_CHANGES and on REOPEN.
   */
  internalRejectNote?: string;
  /**
   * Director-set manual lock override on a single Application
   * (season-phase-deadlines task 3). `LOCKED`/`UNLOCKED` take precedence over
   * the date-based phase close when computing the effective Application_Lock
   * (R16.1–R16.4); an ABSENT attribute (the third state) means the date-based
   * close alone governs. Additive/nullable — no backfill; a row without it is
   * date-governed. Set/cleared only by a Director through the lock ops; clearing
   * REMOVEs the attribute, returning the Application to date-governed behavior.
   */
  manualLockOverride?: ManualLockOverride;
  /**
   * Human-readable applicant name, resolved from the Person SSOT for the
   * director review list ONLY (Req 9.1). NOT persisted — identity lives on
   * Person (meridian-data-model Req 3); `listApplicationResponses` stamps it on
   * the returned rows so the review UI shows a name instead of the raw personId.
   */
  applicantName?: string;
  /**
   * The applicant's division key, resolved from Participation for the director
   * review list ONLY (division tabs/filtering). NOT persisted here — division
   * lives on Participation; `listApplicationResponses` stamps it on the rows.
   */
  divisionKey?: string;
}

/**
 * DocumentCatalog table - One configured document slot for a competition
 * (NEW — application-form-builder Req 20.1).
 * Partition Key: competitionId
 * Sort Key: id (UUID)
 *
 * Uploaded documents link back to a catalog entry via the additive
 * `DocumentReference.catalogEntryId`; the underlying `DocumentReference`
 * storage is unchanged (Req 20.4).
 */
export interface DocumentCatalogEntryItem {
  competitionId: string; // PK
  id: string; // SK (UUID)
  label: string;
  requiredForSubmission: boolean;
  visibleDivisionKeys?: string[] | null;
  applicantTypeScope?: ApplicantType | 'BOTH' | null;
  /**
   * Optional upload constraint — accepted MIME content types for this slot;
   * absent/null = unrestricted (apply-document-uploads Req 5.1, 5.4). Additive:
   * mirrors the GraphQL `DocumentCatalogEntry` type and the pure
   * `documentCatalog.ts#DocumentCatalogEntry`.
   */
  allowedContentTypes?: string[] | null;
  /**
   * Optional upload constraint — maximum file size in bytes; absent/null =
   * unlimited (apply-document-uploads Req 5.2, 5.4).
   */
  maxFileSizeBytes?: number | null;
  /**
   * Optional upload constraint — maximum uploads per slot; absent/null =
   * unlimited (apply-document-uploads Req 5.3, 5.4).
   */
  maxFileCount?: number | null;
  /**
   * Optional mapping to a known `DocumentType`; unset = generic catalog upload
   * (Decision D2).
   */
  documentType?: DocumentType | null;
  // --- application-builder-documents (additive; Req 6.7) ---
  /** Optional applicant-facing instructions shown beneath the uploader (Req 2). */
  helpText?: string | null;
  // --- application-builder-rich-help-text (additive) ---
  /** NEW — formatting indicator; absent ⇒ PLAIN (Req 5.1). */
  helpTextFormat?: HelpTextFormat;
  /** NEW — sanitized allow-listed HTML; present iff helpTextFormat === 'HTML' (Req 2.2). */
  helpTextHtml?: string;
  /** Explicit phase; absent ⇒ derived from requiredForSubmission via effectivePhase (Req 3, 6.4). */
  phase?: 'PHASE_1' | 'PHASE_2' | null;
  /** Dense, ascending presentation order within a competition (Req 4). */
  displayOrder?: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * PageApproval table - Per-page director-approval record (NEW —
 * application-form-builder Req 21.2).
 * Partition Key: applicantPersonId
 * Sort Key: approvalKey = `${competitionId}#${formPageId}`
 * GSI1: competitionPage-applicantPersonId-index (PK `competitionPage`, SK applicantPersonId)
 *
 * Stored per `(applicantPersonId, competitionId, formPageId)` and determined
 * INDEPENDENTLY of the application's `applicationStatus` (Req 21.6, 16.5). The
 * absence of a row is equivalent to the default `PENDING` status.
 */
export interface PageApprovalItem {
  applicantPersonId: string; // PK
  approvalKey: string; // SK = `${competitionId}#${formPageId}`
  /**
   * Composite GSI partition for `competitionPage-applicantPersonId-index` —
   * populated with the value `${competitionId}#${formPageId}` (equal to
   * `approvalKey`). A single clean token so the index name is derivable from
   * its key attributes (R4.6).
   */
  competitionPage: string;
  competitionId: string;
  formPageId: string;
  status: PageApprovalStatus; // default PENDING
  approvalDetails?: {
    approvedBy: string;
    decidedAt: string;
    note?: string;
    fields?: Record<string, string>; // judge-projectable (Req 21.7, 21.8)
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * EmailTemplate table - Managed email templates for automated communications
 * Partition Key: id (UUID)
 * GSI1: templateName-index
 */
export interface EmailTemplate {
  id: string;
  templateName: string;
  subject: string;
  body: string;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  lastEditedBy: string;
}

/**
 * BrandingConfig table - Year-specific branding configuration
 * Partition Key: competitionYear
 */
export interface BrandingConfig {
  competitionYear: number;
  pageantName: string;
  logoS3Key?: string;
  primaryColor: string;
  accentColor: string;
  headerText: string;
  updatedAt: string;
}

/**
 * OrgProductSubscription table - org-level, all-or-nothing Product_Subscription
 * flags (meridian-data-model Phase 7, Req 9.2). There is NO per-user payment:
 * each flag turns a Product on for the entire org. Single-tenant, so this is a
 * single record keyed by a constant org partition.
 *
 * The flag set maps 1:1 to the `OrgProductSubscriptions` domain type in the
 * shared `accessCapabilities` module (the evaluator's Axis 1). The seven
 * Products match `PRODUCT_CATALOG` exactly.
 *
 * Partition Key: orgId (constant ORG_SUBSCRIPTION_PARTITION for the single tenant)
 */
export const ORG_SUBSCRIPTION_PARTITION = 'ORG';

export interface OrgProductSubscriptionItem {
  orgId: string; // PK — constant ORG_SUBSCRIPTION_PARTITION (single-tenant)
  podium: boolean;
  director: boolean;
  merit: boolean;
  envoy: boolean;
  patron: boolean;
  spotlight: boolean;
  vault: boolean;
  updatedAt: string; // ISO 8601
  updatedBy?: string; // admin who last changed the subscription flags
}

// ---------------------------------------------------------------------------
// tenant-foundation — platform-core item types (pooled multi-tenancy + COPPA).
// Additive; DARK until the tenant-foundation migration flips enforcement on.
// `Person`/`User` stay global identity (no tenantId); the tenant boundary is the
// Membership. (tenant-foundation Req 1, 8, 9, 17, 21.)
// ---------------------------------------------------------------------------

/** Tenant: one customer organization — the pooled isolation boundary. PK tenantId. */
export interface TenantItem {
  tenantId: string; // PK
  status: 'active' | 'suspended' | 'closed';
  name: string;
  /**
   * The PUBLIC, human-facing reach identifier (`app.reignara.com/<slug>`), unique
   * across tenants and validated by `_shared/tenantSlug.ts` (tenant-foundation
   * Task 18, Option B). Deliberately SEPARATE from `tenantId` so the public URL
   * can change without touching the internal id every tenant-owned row
   * references. Queried via the `slug-index` GSI.
   *
   * REACH IS SUGAR, NOT A BOUNDARY: a slug only prefills onboarding/join. It must
   * never select the tenant for data access — that is always the server-validated
   * `custom:activeTenantId` claim + Membership.
   *
   * Optional because tenants provisioned before Task 18 predate it; readers must
   * tolerate its absence.
   */
  slug?: string;
  createdAt: string; // ISO 8601
  updatedAt?: string;
}

/** Per-tenant configuration (age policy + COPPA legal knobs + branding). PK tenantId. */
export interface TenantConfigItem {
  tenantId: string; // PK
  // Age bands: age < coppaAge → COPPA minor (managed); coppaAge..majorityAge-1 →
  // teen (parental involvement); age >= majorityAge → adult. Legal-set values.
  coppaAge: number; // default 13 (US COPPA), configurable per tenant
  majorityAge: number; // default 18, configurable per tenant/jurisdiction
  vpcMethod: string; // accepted verifiable-parental-consent method; initial 'signed_form'
  /**
   * The consent form as RENDERED TEXT — presented inline to the guardian in the
   * parental-consent step. Required for a complete policy (Task 15 / Req 11.5).
   * Product decision (2026-07): the form is rendered text rather than a document,
   * so this — not a document ref — is the authoritative content.
   */
  consentFormBody?: string;
  /**
   * OPTIONAL provenance pointer to the source document the body was drafted from
   * (e.g. counsel's file). NOT the presented content and NOT required — kept so a
   * signed artifact can be traced back to its source.
   */
  consentFormTemplateRef?: string;
  /**
   * The privacy-notice version recorded on EVERY Consent_Ledger entry. A DEDICATED
   * knob (not derived from the form/template) so counsel can revise `disclosures`
   * or `consentFormBody` and bump this independently — otherwise two materially
   * different notices could be recorded under the same version, which would
   * undermine the ledger as legal evidence. Required for a complete policy.
   */
  noticeVersion?: string;
  disclosures?: string;
  retentionDays?: number;
  /**
   * Who approved this tenant's legal policy, and when. Recorded by the policy
   * loader so a consent captured under a given notice version is traceable to a
   * human approval rather than to whoever happened to run a script.
   */
  legalPolicyApprovedBy?: string;
  legalPolicyApprovedAt?: string;
  brandingConfigRef?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Membership: the tenant boundary — one human's involvement in one Tenant.
 * Home of all tenant-scoped profile + role + status + (for a minor) consent state.
 * Base PK tenantId / SK personId; GSI personId-tenantId-index (a person's memberships).
 */
export interface MembershipItem {
  tenantId: string; // PK
  personId: string; // SK
  roles: string[]; // tenant-scoped roles (e.g. applicant, contestant, judge, admin, guardian)
  status: 'invited' | 'active' | 'removed' | 'declined';
  // For a minor's membership only — the per-(child, Tenant) consent state (Req 17.6).
  consentState?: 'PENDING' | 'GRANTED' | 'DENIED' | 'REVOKED';
  consentUpdatedAt?: string;
  // Tenant-scoped profile lives HERE, never on Person (bio, headshot, division,
  // paperwork refs, etc.) — additive, product-defined.
  lastActiveAt?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * GuardianLink: guardian↔minor relationship, scoped to a Tenant.
 * Base PK guardianPersonId / SK tenantMinorKey (`${tenantId}#${minorPersonId}`);
 * GSI minorPersonId-tenantId-index (a minor's guardians in a Tenant).
 */
export interface GuardianLinkItem {
  guardianPersonId: string; // PK
  tenantMinorKey: string; // SK — `${tenantId}#${minorPersonId}`
  tenantId: string;
  minorPersonId: string;
  createdAt: string;
}

/**
 * ConsentLedger: append-only COPPA consent events, per (minor, Tenant).
 * Base PK subjectPersonId / SK consentEventKey (`${tenantId}#${recordedAt}#${eventId}`);
 * GSI tenantId-recordedAt-index (a Tenant's consent audit).
 */
export interface ConsentLedgerItem {
  subjectPersonId: string; // PK — the minor
  consentEventKey: string; // SK — `${tenantId}#${recordedAt}#${eventId}`
  tenantId: string;
  recordedAt: string; // ISO 8601
  eventId: string;
  guardianPersonId: string; // the consenting adult
  priorState?: 'PENDING' | 'GRANTED' | 'DENIED' | 'REVOKED';
  newState: 'PENDING' | 'GRANTED' | 'DENIED' | 'REVOKED';
  method: string; // e.g. 'signed_form'
  noticeVersion: string; // the version of the privacy notice shown at consent
  signedFormDocumentRef?: string; // DocumentReference to the stored signed form
  scope?: string;
}

/**
 * SupportGrant: a platform operator's narrow, expiring, READ-ONLY window into one
 * Tenant (tenant-foundation Task 7.1 / Req 15.6). Base PK tenantId / SK
 * operatorPersonId; GSI operatorPersonId-index (an operator's own grants).
 *
 * Deliberately NOT a Membership: a Membership carries roles (and therefore write
 * access) and would list a stranger in the tenant's own member roster. Retained
 * after expiry/revocation — the record that an operator could see a tenant's data,
 * when, and why, is the accountability this mechanism exists to create.
 */
export interface SupportGrantItem {
  tenantId: string; // PK — the tenant this grant opens
  operatorPersonId: string; // SK — the platform operator
  /** ISO 8601. Checked against the SERVER clock at every use. */
  expiresAt: string;
  /** The operator's recorded justification. A blank/short reason invalidates it. */
  reason: string;
  /** Audit provenance: which platform admin minted it, and when. */
  grantedBy: string;
  grantedAt: string; // ISO 8601 — also the GSI sort key
  /** Presence terminates the grant immediately, regardless of `expiresAt`. */
  revokedAt?: string;
  revokedBy?: string;
}

/**
 * InquiryResponse table - Admin responses to contact form inquiries
 * Partition Key: id (UUID)
 * GSI: inquiryId-createdAt-index
 */
export interface InquiryResponse {
  id: string;
  inquiryId: string;
  adminEmail: string;
  adminName: string;
  responseText: string;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/**
 * SystemLog table - Persistent audit log for admin/system actions
 * Partition Key: id (UUID)
 * GSI: timestamp-index (action + timestamp) for filtering by action type and sorting by time
 */
export interface SystemLogEntry {
  id: string;
  timestamp: string; // ISO 8601
  action: string;
  performedBy: string;
  targetName: string;
  targetEmail: string;
  targetId: string;
  details?: string;
}

/**
 * CalendarEvent table - Pageant calendar events
 * Partition Key: id (UUID)
 * GSI: competitionYear-date-index for querying events by year sorted by date
 */
export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  time: string;
  location: string;
  description?: string;
  competitionYear: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Channel table - Messaging channels for communication
 * Partition Key: id (UUID)
 *
 * The `members` field stores `cognitoSub` values (not email addresses).
 * Members are resolved from email to cognitoSub via the User table
 * email-index GSI at channel creation/update time. This ensures membership
 * remains stable when a user changes their email address.
 */
export interface Channel {
  id: string;
  name: string;
  type: string; // 'PUBLIC' | 'DIRECT'
  description?: string;
  /** List of cognitoSub values identifying channel members */
  members: string[];
  createdBy: string; // cognitoSub of creator
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  lastMessageText?: string;
  lastMessageAt?: string; // ISO 8601
}

/**
 * ChannelMembership table - Per-user read state for messaging channels
 * Partition Key: channelId
 * Sort Key: userId (email address)
 * GSI: userId-index (userId + channelId)
 */
export interface ChannelMembership {
  channelId: string;
  userId: string; // email address
  lastReadAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/**
 * User table — a THIN authentication / display-projection of the durable
 * `Person` SSOT (meridian-data-model Phase 6, task 12.4; decision R7.3 =
 * THIN PROJECTION, NOT fully-folded).
 *
 * `Person` (Phase 1) is the single source of truth for human identity. `User`
 * is retained only as the Cognito-anchored authentication anchor plus a small
 * display cache, so the `email-index` and its consumers (e.g. `Channel.members`
 * resolution by email) keep working unchanged during and after migration. Those
 * `email-index` consumers are deliberately NOT repointed in this phase — doing
 * so is the rejected "fully fold `User` into `Person`" option.
 *
 * Retained attributes (the thin projection):
 *   - `cognitoSub`  — Cognito auth anchor (PK).
 *   - `email`       — Cognito email claim + `email-index` lookup key (auth).
 *   - `fullName`    — display-name cache (a projection of `Person.fullName`).
 *   - `role`        — Cognito-group display cache only; NOT the access SSOT.
 *                     `RoleAssignment` owns access (Requirement 2); this column
 *                     is a cache of the projected Cognito group.
 *   - `lastLoginAt` — authentication activity timestamp.
 *   - `createdAt` / `updatedAt` — record audit timestamps.
 *
 * Excluded — owned by `Person`, never STORED on `User` (Requirements 1.3, 7.3):
 *   `legalFirstName`, `legalLastName`, `preferredName`, `primaryEmail` (the
 *   identity-SSOT email, distinct from the Cognito `email` claim retained
 *   above), `mobilePhone`, `homePhone`, and `profilePhotoS3Key`. In particular
 *   `profilePhotoS3Key` is now COMPUTED from the `Person` record by
 *   Person_Service (task 10.1, Requirements 4.1–4.3) rather than stored here.
 *
 * Partition Key: cognitoSub
 * GSI1: email-index (email)
 * GSI2: role-fullName-index (role + fullName)
 */
export interface User {
  cognitoSub: string;
  email: string;
  fullName: string;
  role: string; // Cognito group display cache: 'Admins' | 'Judges' (legacy 'PowerUsers'/'Applicants' retired)
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  lastLoginAt?: string; // ISO 8601
}

/**
 * FeeCatalog table - Master fee definitions per competition year
 * Partition Key: competitionYear
 * Sort Key: feeId
 */
export interface FeeCatalogItem {
  competitionYear: number;
  feeId: string;
  name: string;
  description?: string;
  amount: number; // cents
  statusTrigger: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ANY';
  required: boolean;
  dueDate?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * ContestantFee table - Per-contestant fee assignments and payment status
 * Partition Key: visitorKey ({applicantId}#{competitionYear})
 * Sort Key: feeId
 * GSI: paymentIntentId-index (paymentIntentId)
 */
export interface ContestantFeeItem {
  visitorKey: string; // {applicantId}#{competitionYear}
  feeId: string;
  applicantId: string;
  competitionYear: number;
  feeName: string;
  amount: number; // cents, discounted if applicable
  originalAmount?: number; // cents, set when discount applied
  statusTrigger: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ANY';
  required: boolean;
  status: 'PENDING' | 'PAID' | 'WAIVED' | 'PAYMENT_FAILED' | 'REFUNDED';
  paymentIntentId?: string;
  paidAt?: string;
  dueDate?: string;
  discountRuleId?: string;
  discountName?: string;
  createdAt: string;
}

/**
 * Payment table - Stripe payment intent records
 * Partition Key: paymentIntentId
 * GSI: applicantId-competitionYear-index (applicantId + competitionYear)
 */
export interface PaymentItem {
  paymentIntentId: string;
  applicantId: string;
  competitionYear: number;
  amount: number; // cents
  currency: string;
  status: 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'REFUNDED';
  feeIds: string[];
  discountRuleId?: string;
  receiptUrl?: string;
  createdAt: string;
  paidAt?: string;
  refundedAt?: string;
}

/**
 * DiscountRule table - Discount and waiver rule definitions per competition year
 * Partition Key: competitionYear
 * Sort Key: ruleId
 * GSI: code-index (code)
 */
export interface DiscountRuleItem {
  competitionYear: number;
  ruleId: string;
  name: string;
  type: 'WAIVER' | 'PERCENT' | 'FIXED_AMOUNT';
  value: number;
  appliesTo: 'ALL_FEES' | 'SPECIFIC_FEE' | 'SPECIFIC_STATUS';
  targetFeeId?: string;
  targetStatus?: string;
  eligibility: 'AUTO' | 'CODE_REQUIRED' | 'BOTH';
  eligibilityCondition?: string;
  code?: string;
  maxUses?: number;
  currentUses: number;
  expiresAt?: string;
  active: boolean;
  createdAt: string;
}

/**
 * DiscountRedemption table - Tracks which discounts each contestant has redeemed
 * Partition Key: visitorKey ({applicantId}#{competitionYear})
 * Sort Key: ruleId
 */
export interface DiscountRedemptionItem {
  visitorKey: string; // {applicantId}#{competitionYear}
  ruleId: string;
  redeemedAt: string;
  paymentIntentId: string;
}

/**
 * InstagramToken table - Phyllo-based Instagram link status per contestant
 * Partition Key: cognitoSub
 *
 * Stores Phyllo identifiers instead of raw Instagram access tokens.
 * Phyllo manages the actual Instagram tokens internally.
 */
export interface InstagramToken {
  cognitoSub: string;
  phylloUserId: string;
  phylloAccountId?: string;
  instagramUsername?: string;
  linkedAt?: string; // ISO 8601
  linkStatus: 'connected' | 'disconnected' | 'not_connected';
}

/**
 * InstagramMetrics table - Daily Instagram metrics snapshots per contestant
 * Partition Key: cognitoSub (String)
 * Sort Key: snapshotDate (String, YYYY-MM-DD)
 * GSI: snapshotDate-index (snapshotDate + cognitoSub)
 */
export interface InstagramMetrics {
  cognitoSub: string;
  snapshotDate: string;
  instagramUsername: string;
  followerCount: number;
  engagementRate: number;
  totalPosts: number;
  averageLikesPerPost: number;
  averageCommentsPerPost: number;
  reachTotal: number;
  impressionsTotal: number;
  fetchedAt: string;
}

/**
 * ContestantSeasonBaseline table - Immutable per-season baseline snapshot of Instagram metrics
 * captured at the moment a contestant's ApplicantYearProfile is approved.
 * Partition Key: cognitoSub (String)
 * Sort Key: competitionYear (Number)
 *
 * One row per (contestant, competition season). Baselines are immutable once captured —
 * re-approving within the same season never shifts the baseline, and approving in a new
 * competition year creates a new baseline row without affecting prior-season rows.
 * Season-scoped metrics are computed on read as max(0, currentSnapshot.M - baseline.M).
 */
export interface ContestantSeasonBaseline {
  cognitoSub: string;
  competitionYear: number;
  followerCount: number;
  totalPosts: number;
  reachTotal: number;
  impressionsTotal: number;
  instagramUsername: string;
  capturedAt: string; // ISO 8601
}

/**
 * DeviceToken table - Push notification device tokens
 * Partition Key: userId (Cognito sub)
 * Sort Key: platform#token (composite: "ios#ExponentPushToken[xxx]" or "android#ExponentPushToken[xxx]")
 * TTL: expiresAt (90 days from registration)
 */
export interface DeviceToken {
  userId: string;           // Cognito sub (PK)
  'platform#token': string; // Composite sort key
  platform: string;         // 'ios' | 'android'
  token: string;            // Expo push token
  createdAt: string;        // ISO 8601
  updatedAt: string;        // ISO 8601
  expiresAt: number;        // Unix epoch (TTL)
}

// ===========================================================================
// Sponsor CRM (meridian-data-model Phase 7, task 13.5)
// ---------------------------------------------------------------------------
// The sponsor account model: SponsorOrg (the account), Sponsorship (the
// per-Season commitment), SponsorContact (the Person↔org link), and the public
// sponsor Inquiry (the intake record). These are ADDITIVE shapes — the Patron
// portal UI is deferred (task 16.5 placeholder).
//
// Identity rule (Req 11.1, 11.2, 11.3): a SponsorContact "points up" at the
// durable Person SSOT (Phase 1). It stores ZERO identity fields — name, email,
// and phone live on the linked Person. Intake resolves the Person through the
// SAME Person uniqueness rules (Requirement 1) so a public inquiry never
// creates a duplicate human for an email that already exists, and a contact's
// Person MAY have a null `cognitoSub` (a non-authenticating contact who can
// later claim a login — the claim/merge path links the `cognitoSub` to that
// existing Person rather than creating a second one).
//
// Access rule (Req 11.5): "sponsor" is NOT a Role. It is a contact facet plus
// the Patron product permission (own-agreement scope) — see
// `sponsorPatronEntitlement` in
// `infrastructure/lambda/sponsor-service/sponsorLogic.ts`.
// ===========================================================================

/** SponsorOrg lifecycle status (Req 11.1). Intake creates `prospect`; an admin
 *  conversion sets `active`; a lapsed relationship is `lapsed`. */
export enum SponsorOrgStatus {
  PROSPECT = 'prospect',
  ACTIVE = 'active',
  LAPSED = 'lapsed',
}

/** Sponsorship commitment type (Req 11.1). `in_kind` covers custom
 *  goods/services deals; `cash` is a monetary commitment. */
export enum SponsorshipType {
  CASH = 'cash',
  IN_KIND = 'in_kind',
}

/** Public sponsor-inquiry package type (Req 11.2). */
export enum SponsorInquiryType {
  STANDARD_PACKAGE = 'standard_package',
  CUSTOM = 'custom',
}

/** Public sponsor-inquiry triage status. An inquiry is `NEW` on submit and
 *  `CONVERTED` once an admin converts the prospect org → active (Req 11.4). */
export enum SponsorInquiryStatus {
  NEW = 'NEW',
  CONVERTED = 'CONVERTED',
}

/**
 * SponsorOrg table — the sponsor account and SSOT for the sponsor relationship
 * (Req 11.1). Created with `status = prospect` by public intake (Req 11.2) and
 * flipped to `active` by an admin conversion (Req 11.4).
 *
 * Partition Key: id (UUID)
 * GSI1: status-createdAt-index — admin listing of prospects/active orgs,
 *       newest-first (index name == its key attributes, Req 4.6).
 */
export interface SponsorOrgItem {
  id: string; // PK (UUID)
  company: string;
  status: SponsorOrgStatus;
  createdAt: string; // ISO 8601 (GSI sort key)
  updatedAt: string; // ISO 8601
  deletedAt?: string; // ISO 8601 soft delete (null = active record)
}

/**
 * Sponsorship table — the SponsorOrg↔Season commitment (Req 11.1, 11.4). One
 * per (sponsorOrgId, competitionYear, id); recorded when an admin converts a
 * prospect org.
 *
 * Partition Key: id (UUID)
 * GSI1: sponsorOrgId-competitionYear-index — list an org's commitments by
 *       season (index name == its key attributes, Req 4.6).
 */
export interface SponsorshipItem {
  id: string; // PK (UUID)
  sponsorOrgId: string; // GSI: sponsorOrgId-competitionYear-index (partition)
  competitionYear: number; // GSI sort key (four-digit year)
  tier: string;
  amount?: number; // cents (optional for in-kind)
  type: SponsorshipType;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/**
 * SponsorContact table — the Person↔SponsorOrg link with a `title` (Req 11.1).
 * "Points up" at the durable Person SSOT and stores ZERO identity fields; the
 * linked Person MAY have a null `cognitoSub` (a contact-only, non-auth human
 * who can later claim a login via the claim/merge path, Req 11.3).
 *
 * Partition Key: id (UUID)
 * GSI1: sponsorOrgId-index — list an org's contacts.
 * GSI2: personId-index — find the sponsor contacts for a Person (used by the
 *       claim/merge path and the directory visibility policy, Req 10.3).
 */
export interface SponsorContactItem {
  id: string; // PK (UUID)
  sponsorOrgId: string; // GSI: sponsorOrgId-index
  personId: string; // GSI: personId-index — the Person SSOT (may have null cognitoSub)
  title?: string;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  deletedAt?: string; // ISO 8601 soft delete (null = active record)
}

/**
 * SponsorInquiry table — the public sponsor-inquiry intake record (Req 11.2).
 * Distinct from the contact-form `Inquiry`/`InquiryResponse` tables: this is the
 * sponsor-funnel record that, on submit, creates a `SponsorOrg(prospect)` plus a
 * `SponsorContact` whose Person is resolved through the Person uniqueness rules.
 *
 * It carries the raw public submission (company + the contact's identity fields)
 * so intake can resolve/create the Person; once intake runs, the resulting
 * `sponsorOrgId` / `sponsorContactId` / `personId` are stamped back onto the
 * record for traceability.
 *
 * Partition Key: id (UUID)
 * GSI1: status-createdAt-index — admin triage listing, newest-first (index name
 *       == its key attributes, Req 4.6).
 */
export interface SponsorInquiryItem {
  id: string; // PK (UUID)
  type: SponsorInquiryType; // standard_package | custom
  status: SponsorInquiryStatus; // NEW on submit; CONVERTED after admin conversion
  company: string;
  // Raw public submission used to resolve/create the contact Person (Req 11.2).
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone?: string;
  contactTitle?: string;
  packageTier?: string; // requested tier for a standard_package inquiry
  message?: string; // free-text details (custom deals)
  // Stamped once intake has created the prospect org + contact (Req 11.2).
  sponsorOrgId?: string;
  sponsorContactId?: string;
  personId?: string;
  createdAt: string; // ISO 8601 (GSI sort key)
  updatedAt: string; // ISO 8601
}

// ===========================================================================
// Director Document Library (director-document-library)
// ---------------------------------------------------------------------------
// The governing principle is "permissions are computed, not stored": every
// document is anchored to a domain entity and visibility is a pure function of
// (caller role/identity, anchor / Document_Scope). There are NO user-created
// folders, NO per-file access-control lists, and NO ad-hoc share links.
//
// `LibraryDocument` is the ONLY net-new persisted DOMAIN entity introduced by
// this feature (Req 4.6); `DownloadRateLimitEntry` is an operational TTL
// counter that backs download-URL rate limiting (Req 3.8). Contestant uploads
// are read THROUGH the existing `DocumentReference` table (above) — this
// feature adds no fields to it and never duplicates or migrates it.
// ===========================================================================

/**
 * LibraryDocument table — a director-uploaded reference document distributed to
 * a COMPUTED audience (the contestants in its Document_Scope) plus
 * directors/admins. The ONLY net-new persisted domain entity in this feature
 * (Req 4.6). Additive table; single-tenant (no `tenantId`); legacy data
 * untouched.
 *
 * Partition Key: id (UUID)
 * GSI1: scopeAnchorKey-updatedAt-index — list a Season's/Competition's library
 *       documents newest→oldest (Req 6.1); index name == its key attributes.
 *
 * Lifecycle:
 *   - Soft delete via `deletedAt`: the read path filters it and the download
 *     path rejects it; there is NO un-delete affordance in the Document_Center,
 *     so removal is non-reversible through the UI without destroying audit
 *     history (Req 6.3).
 *   - `version` is the optimistic-concurrency token: writes use a conditional
 *     compare-and-set so concurrent edits serialize to a single committed
 *     state (Req 6.8).
 *   - Only the CURRENT `s3Key` is ever resolved on download, so a replaced or
 *     removed object key is never issued a URL (Req 6.7).
 */
export interface LibraryDocument {
  id: string; // PK (UUID)
  title: string; // trimmed, 1..200 chars (Req 4.1, 4.9)

  // ---- Document_Scope (Req 4.2, 4.3) ----
  /** The scope anchor kind — exactly one Season OR exactly one Competition. */
  scopeAnchorType: 'SEASON' | 'COMPETITION';
  /** The anchor identifier: the competitionYear (as a string) OR the competitionId. */
  scopeAnchorId: string;
  /** Resolved Season year; always set and equal to the anchor's Season year. */
  competitionYear: number;
  /** GSI1 partition: `SEASON#<year>` | `COMPETITION#<competitionId>` (Req 6.1). */
  scopeAnchorKey: string;
  /** Optional Division narrowing; each key must belong to the anchor (Req 4.3, 4.10). */
  divisionKeys?: string[];
  /** Optional applicant-type narrowing; each must belong to the anchor (Req 4.3, 4.10). */
  applicantTypes?: ApplicantType[];

  // ---- Stored object + file metadata (Req 4.5) ----
  /** Current stored object only: `library/{cognitoSub}/{uuid}.{ext}`. */
  s3Key: string;
  /** Original file name, stored exactly. */
  fileName: string;
  /** A Supported_File_Type, server re-validated against actual bytes (Req 5.7). */
  contentType: string;
  /** Actual stored size in bytes, server re-validated (Req 5.7). */
  sizeBytes: number;

  // ---- Provenance / lifecycle ----
  createdBy: string; // director Cognito sub
  uploadedAt: string; // ISO 8601 — set/updated on create + replace (Req 6.2)
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601 — drives GSI1 ordering (Req 6.1, 6.2)
  version: number; // optimistic-concurrency token for serialized edits (Req 6.8)
  deletedAt?: string; // ISO 8601 soft-delete tombstone; read + download filter it (Req 6.3)
}

/**
 * DownloadRateLimit table — a fixed-window rate counter for download-URL
 * issuance (Req 3.8). Keyed by caller + 60-second window; TTL-expired. An
 * atomic conditional increment rejects the 61st request within a window so a
 * director submitting more than 60 requests in any 60-second window is denied
 * each further URL for the remainder of that window.
 *
 * Partition Key: pk = `${callerSub}#${windowStartEpoch60s}`
 * TTL attribute: expiresAt (windowStart + 120s)
 */
export interface DownloadRateLimitEntry {
  pk: string; // `${callerSub}#${windowStartEpoch60s}`
  count: number; // atomic ADD; reject when it would exceed 60
  expiresAt: number; // Unix epoch (TTL = windowStart + 120s)
}

/**
 * Title — a director-owned, runtime-configurable, season-scoped catalog entry
 * (season-scoped-title-registry). Mirrors the `Division` shape: it is the
 * `${stage}-TitleRegistry` table row, keyed by `competitionYear`. The catalog
 * is the source of title *definitions*; `Participation.approvedTitle` and
 * `ApplicationResponse.titlePreferences` remain the source of *assignments*.
 */
export interface Title {
  /** UUID — the table partition key. */
  id: string;
  /** The competition year that scopes this title (GSI partition key). */
  competitionYear: number;
  /** Stable machine key: 1..64 chars, `[a-z0-9-]`; unique per competitionYear. */
  key: string;
  /** Human-readable display name: 1..100 chars; editable via rename. */
  label: string;
  /** Defaults to true on create; toggled by activate/deactivate. */
  active: boolean;
  /** Contiguous `1..N` ordinal within the competition year. */
  displayOrder: number;
  /** Optional applicant-type scope (e.g. MISS/MRS) restricting selectability. */
  applicantType?: string | null;
  /** Optional Division-key scope restricting selectability. */
  divisionKey?: string | null;
  createdAt: string;
  updatedAt: string;
}
